import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface VendorHelpContent {
  content: string;
  lastUpdated: string;
}

export const useVendorHelp = () => {
  const { toast } = useToast();
  const [vendorHelp, setVendorHelp] = useState<VendorHelpContent>({
    content: "<h1>Vendor Help</h1><p>Welcome to the vendor portal. Here you can manage your services, bookings, and calendar.</p>",
    lastUpdated: new Date().toISOString()
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch vendor help content
  const fetchVendorHelpContent = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('admin_settings')
        .select('settings')
        .eq('id', 1)
        .single();

      if (error) {
        console.error('Error fetching vendor help content:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load vendor help content"
        });
        return;
      }

      if (data?.settings?.vendorHelp) {
        setVendorHelp({
          content: data.settings.vendorHelp.content || vendorHelp.content,
          lastUpdated: data.settings.vendorHelp.lastUpdated || new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error in fetchVendorHelpContent:', error);
      toast({
        variant: "destructive", 
        title: "Error",
        description: "Failed to load vendor help content"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Save vendor help content
  const saveVendorHelpContent = async (content: string): Promise<boolean> => {
    try {
      setIsSaving(true);
      
      const updatedVendorHelp = {
        content,
        lastUpdated: new Date().toISOString()
      };

      const { error } = await supabase
        .from('admin_settings')
        .update({
          settings: {
            vendorHelp: updatedVendorHelp
          }
        })
        .eq('id', 1);

      if (error) {
        console.error('Error saving vendor help content:', error);
        toast({
          variant: "destructive",
          title: "Error", 
          description: "Failed to save vendor help content"
        });
        return false;
      }

      setVendorHelp(updatedVendorHelp);
      
      toast({
        title: "Success",
        description: "Vendor help content updated successfully"
      });
      
      return true;
    } catch (error) {
      console.error('Error in saveVendorHelpContent:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save vendor help content"
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchVendorHelpContent();
  }, []);

  return {
    vendorHelp,
    isLoading,
    isSaving,
    fetchVendorHelpContent,
    saveVendorHelpContent
  };
};